import {DatePicker, Form, Select} from 'antd';
import {Dayjs} from 'dayjs';
import {NoUndefinedRangeValueType} from 'rc-picker/lib/PickerInput/RangePicker';

import {FormActiveMark} from '../../../../../../component/form-active-mark/form-active-mark';
import {SelectWithCheckboxes} from '../../../../../../component/select-with-checkboxes/select-with-checkboxes';
import {Locale} from '../../../../../../provider/locale/locale';
import {LocaleContextType} from '../../../../../../provider/locale/locale-context-type';
import {ReviewReplierType} from '../../../../../../service/reviews/reviews-repliers';
import {ResponsibleUserType} from '../../../../../../service/reviews/reviews-responsible-users';
import {handleFilterOption} from '../../../../../../util/antd/select-helper';
import * as styles from '../reviews-filter.scss';
import {reviewsFilterDropdownStyle, reviewsFilterGetPopupContainer} from '../reviews-filter-popup-helper';
import {EmployeesReviewsEnum, ReviewsFilterDataType, ReviewsFilterFieldNameEnum} from '../reviews-filter-type';

const {Option} = Select;

type PropsType = {
    reviewFilterState: ReviewsFilterDataType;
    responsibleUsersList: {results: Array<ResponsibleUserType>} | undefined;
    isReviewResponsibleUsersInProgress: boolean;
    reviewsReplierList:
        | {repliers?: Array<ReviewReplierType>; internalAgencyRepliers?: Array<ReviewReplierType>}
        | undefined;
    reviewsReplierListIsInProgress: boolean;
    internalAgencyReviewLicense: boolean | undefined;
    watchedReplyDateFrom: Dayjs | null | undefined;
    watchedReplyDateTo: Dayjs | null | undefined;
    replyDateFrom: Dayjs | null;
    replyDateTo: Dayjs | null;
    getLocalizedString: LocaleContextType['getLocalizedString'];
    onResponsibleEmployeeChange: (value: Array<number | string>) => void;
    onRespondingEmployeeChange: (value: Array<number | string>) => void;
    onAgencyRepliersChange: (value: Array<number | string>) => void;
    onEmployeeReplyDateRangeChange: (date: NoUndefinedRangeValueType<Dayjs> | null) => void;
};

export function ReviewsFilterSectionEmployee(props: PropsType): JSX.Element {
    const {
        reviewFilterState,
        responsibleUsersList,
        isReviewResponsibleUsersInProgress,
        reviewsReplierList,
        reviewsReplierListIsInProgress,
        internalAgencyReviewLicense,
        watchedReplyDateFrom,
        watchedReplyDateTo,
        replyDateFrom,
        replyDateTo,
        getLocalizedString,
        onResponsibleEmployeeChange,
        onRespondingEmployeeChange,
        onAgencyRepliersChange,
        onEmployeeReplyDateRangeChange,
    } = props;

    return (
        <>
            <div className={styles.ReviewsFilter_selectFilter}>
                <FormActiveMark
                    isVisible={reviewFilterState[ReviewsFilterFieldNameEnum.employeesResponsible].length > 0}
                />
                <Form.Item
                    initialValue={reviewFilterState[ReviewsFilterFieldNameEnum.employeesResponsible]}
                    label={<Locale stringKey="REVIEWS__FILTER__LABEL__RESPONSIBLE" />}
                    name={ReviewsFilterFieldNameEnum.employeesResponsible}
                >
                    <Select<Array<string | number>>
                        disabled={isReviewResponsibleUsersInProgress}
                        dropdownStyle={reviewsFilterDropdownStyle}
                        filterOption={(input, option) => handleFilterOption(input, option, true)}
                        getPopupContainer={reviewsFilterGetPopupContainer}
                        loading={isReviewResponsibleUsersInProgress}
                        mode="multiple"
                        onChange={onResponsibleEmployeeChange}
                        placeholder={<Locale stringKey="REVIEWS__FILTER__SELECT_AN_EMPLOYEE" />}
                    >
                        <Option value={EmployeesReviewsEnum.all}>
                            <Locale stringKey="REVIEWS__FILTER__ANY_EMPLOYEE" />
                        </Option>
                        <Option value={EmployeesReviewsEnum.noSelected}>
                            <Locale stringKey="REVIEWS__RESPONSIBLE_USER__NOT_SELECTED" />
                        </Option>
                        {responsibleUsersList?.results.map((reviewsResponsible: ResponsibleUserType): JSX.Element => {
                            const {email, fullName, id} = reviewsResponsible;

                            return (
                                <Option key={email + fullName} value={String(id)}>
                                    {email}

                                    {Boolean(fullName) && (
                                        <>
                                            <br />
                                            <span className={styles.ReviewsFilter_username}>{fullName}</span>
                                        </>
                                    )}
                                </Option>
                            );
                        })}
                    </Select>
                </Form.Item>
            </div>

            <div className={styles.ReviewsFilter_selectFilter}>
                <FormActiveMark
                    isVisible={reviewFilterState[ReviewsFilterFieldNameEnum.employeesResponding].length > 0}
                />
                <Form.Item
                    initialValue={reviewFilterState[ReviewsFilterFieldNameEnum.employeesResponding]}
                    label={<Locale stringKey="REVIEWS__FILTER__LABEL__EMPLOYEE_RESPONDING" />}
                    name={ReviewsFilterFieldNameEnum.employeesResponding}
                >
                    <Select<Array<string | number>>
                        disabled={reviewsReplierListIsInProgress}
                        dropdownStyle={reviewsFilterDropdownStyle}
                        filterOption={(input, option) => handleFilterOption(input, option, true)}
                        getPopupContainer={reviewsFilterGetPopupContainer}
                        loading={reviewsReplierListIsInProgress}
                        mode="multiple"
                        onChange={onRespondingEmployeeChange}
                        placeholder={<Locale stringKey="REVIEWS__FILTER__SELECT_AN_EMPLOYEE" />}
                        virtual={false}
                    >
                        {reviewsReplierList?.repliers?.map((reviewsReplier: ReviewReplierType): JSX.Element | null => {
                            const {email, fullName, id} = reviewsReplier;

                            if (!fullName) {
                                return null;
                            }

                            return (
                                <Option key={email + fullName} value={String(id)}>
                                    {email}

                                    {Boolean(fullName) && (
                                        <>
                                            <br />
                                            <span className={styles.ReviewsFilter_username}>{fullName}</span>
                                        </>
                                    )}
                                </Option>
                            );
                        })}
                    </Select>
                </Form.Item>
            </div>

            {internalAgencyReviewLicense &&
                reviewsReplierList?.internalAgencyRepliers &&
                reviewsReplierList.internalAgencyRepliers.length > 0 && (
                    <div className={styles.ReviewsFilter_selectFilter}>
                        <FormActiveMark
                            isVisible={reviewFilterState[ReviewsFilterFieldNameEnum.agencyRepliers].length > 0}
                        />
                        <Form.Item
                            initialValue={reviewFilterState[ReviewsFilterFieldNameEnum.agencyRepliers]}
                            label={<Locale stringKey="REVIEWS__FILTER__INTERNAL_AGENCY__LABEL" />}
                            name={ReviewsFilterFieldNameEnum.agencyRepliers}
                        >
                            <SelectWithCheckboxes
                                initialValue={reviewFilterState[ReviewsFilterFieldNameEnum.agencyRepliers] || []}
                                isShowPlaceHolderInSelectAllButton={false}
                                onConfirm={(value) => onAgencyRepliersChange(value)}
                                options={(reviewsReplierList.internalAgencyRepliers || []).map((replier) => ({
                                    value: replier?.id.toString(),
                                    label: replier?.fullName || replier.email || '',
                                }))}
                                placeholder={getLocalizedString('REVIEWS__FILTER__SELECT_AN_EMPLOYEE')}
                                selectAllTitle={<Locale stringKey="DROPDOWN_WITH_CONFIRM_COUNT_ALL" />}
                                withoutIndeterminate
                            />
                        </Form.Item>
                    </div>
                )}

            <div className={styles.ReviewsFilter_selectFilter}>
                <FormActiveMark isVisible={Boolean(replyDateFrom) && Boolean(replyDateTo)} />
                <Form.Item label={<Locale stringKey="REVIEWS__FILTER__LABEL__EMPLOYEE_REPLY_DATE" />}>
                    <DatePicker.RangePicker
                        allowEmpty={[true, true]}
                        className={styles.ReviewsFilter_datepicker}
                        format="YYYY-MM-DD"
                        onChange={onEmployeeReplyDateRangeChange}
                        placeholder={[getLocalizedString('TEXT__DATE_FROM'), getLocalizedString('TEXT__DATE_TO')]}
                        placement="topRight"
                        value={[watchedReplyDateFrom ?? replyDateFrom, watchedReplyDateTo ?? replyDateTo]}
                    />
                </Form.Item>
            </div>
        </>
    );
}
